/**
 * The local database (docs/09 §9.2).
 *
 * Stores, and why each exists:
 *   isolates_local   pending / syncing / synced isolates this user entered
 *   reference_data   cached organism, antibiotic, ward and specimen lists
 *   cached_isolates  read-only snapshots for offline viewing, bounded LRU
 *   session          the access token and the signed-in user
 *   audit_queue      local record of offline edits, until they sync
 *
 * Principle 1 of docs/09 §9.1: the client is authoritative for its own queue.
 * Every isolate gets a client UUID at creation and keeps it for life.
 */

import Dexie, { type Table } from "dexie";
import type {
  IsolatePayload,
  Isolate,
  LocalIsolate,
  ReferenceBundle,
  SyncState,
  User,
} from "./types";
import { uuid } from "./utils";

/** docs/09 §9.9: the read-only cache is capped at the 500 most recent rows. */
export const CACHED_ISOLATE_LIMIT = 500;

/** Synced rows older than this are dropped from the local queue (docs/09 §9.9). */
export const SYNCED_RETENTION_DAYS = 30;

export interface CachedIsolate {
  id: string;
  isolate: Isolate;
  seen_at: number;
}

export interface SessionRow {
  key: "current";
  access_token: string | null;
  user: User | null;
  saved_at: number;
}

export interface ReferenceRow {
  key: "current";
  bundle: ReferenceBundle;
  fetched_at: number;
}

export interface AuditQueueRow {
  id?: number;
  client_uuid: string;
  action: string;
  changes: Record<string, { old: unknown; new: unknown }>;
  occurred_at: string;
}

export class AntibiogramDB extends Dexie {
  isolates_local!: Table<LocalIsolate, string>;
  cached_isolates!: Table<CachedIsolate, string>;
  reference_data!: Table<ReferenceRow, string>;
  session!: Table<SessionRow, string>;
  audit_queue!: Table<AuditQueueRow, number>;

  constructor(name = "eksuth-antibiogram") {
    super(name);
    this.version(1).stores({
      // Indexed on state so the sync worker can find pending rows without a
      // full scan, and on updated_at for the "recent entries" list.
      isolates_local: "client_uuid, state, updated_at, record_id",
      cached_isolates: "id, seen_at",
      reference_data: "key",
      session: "key",
      audit_queue: "++id, client_uuid, occurred_at",
    });
  }
}

export const db = new AntibiogramDB();

// ---------------------------------------------------------------------------
// the local queue
// ---------------------------------------------------------------------------

/** Save a newly entered isolate locally. Returns its client UUID. */
export async function saveLocalIsolate(
  payload: IsolatePayload,
  options: { state?: SyncState; clientUuid?: string } = {},
): Promise<string> {
  const now = new Date().toISOString();
  const row: LocalIsolate = {
    client_uuid: options.clientUuid ?? uuid(),
    payload,
    state: options.state ?? "pending",
    record_id: null,
    server_id: null,
    client_created_at: now,
    updated_at: now,
    error: null,
    attempts: 0,
  };
  await db.isolates_local.put(row);
  return row.client_uuid;
}

/** Queue an edit to an already-synced isolate (O-09, docs/09 §9.7). */
export async function queueEdit(
  serverId: string,
  clientUuid: string,
  payload: IsolatePayload,
  expectedUpdatedAt: string | null,
): Promise<void> {
  const now = new Date().toISOString();
  await db.isolates_local.put({
    client_uuid: clientUuid,
    payload,
    state: "pending",
    record_id: null,
    server_id: serverId,
    client_created_at: now,
    updated_at: now,
    error: null,
    attempts: 0,
    editOf: serverId,
    expectedUpdatedAt,
  });
}

export async function markState(
  clientUuid: string,
  state: SyncState,
  patch: Partial<LocalIsolate> = {},
): Promise<void> {
  await db.isolates_local.update(clientUuid, {
    state,
    updated_at: new Date().toISOString(),
    ...patch,
  });
}

/** Rows waiting to go to the server, oldest first. */
export async function pendingIsolates(limit = 50): Promise<LocalIsolate[]> {
  const rows = await db.isolates_local
    .where("state")
    .anyOf("pending", "error")
    .toArray();
  rows.sort((a, b) => a.client_created_at.localeCompare(b.client_created_at));
  return rows.slice(0, limit);
}

export async function countByState(): Promise<Record<SyncState, number>> {
  const counts: Record<SyncState, number> = {
    draft: 0,
    pending: 0,
    syncing: 0,
    synced: 0,
    conflict: 0,
    error: 0,
  };
  await db.isolates_local.each((row) => {
    counts[row.state] += 1;
  });
  return counts;
}

/** The volunteer's most recent entries, for the home screen (docs/08 §8.2). */
export async function recentLocalIsolates(limit = 10): Promise<LocalIsolate[]> {
  const rows = await db.isolates_local.orderBy("updated_at").reverse().toArray();
  return rows.slice(0, limit);
}

/**
 * docs/09 §9.9: drop synced rows older than 30 days. They live on the server
 * and stay visible through the master view, so nothing is lost.
 */
export async function pruneSynced(
  retentionDays = SYNCED_RETENTION_DAYS,
): Promise<number> {
  const cutoff = new Date(Date.now() - retentionDays * 86_400_000).toISOString();
  const stale = await db.isolates_local
    .where("state")
    .equals("synced")
    .filter((row) => row.updated_at < cutoff)
    .toArray();

  await db.isolates_local.bulkDelete(stale.map((row) => row.client_uuid));
  return stale.length;
}

// ---------------------------------------------------------------------------
// the read-only cache
// ---------------------------------------------------------------------------

/** Remember isolates the user has looked at, for offline viewing. */
export async function cacheIsolates(isolates: Isolate[]): Promise<void> {
  const now = Date.now();
  await db.cached_isolates.bulkPut(
    isolates.map((isolate) => ({ id: isolate.id, isolate, seen_at: now })),
  );
  await evictCache();
}

/** Bounded LRU: keep the most recently seen rows, drop the rest. */
export async function evictCache(limit = CACHED_ISOLATE_LIMIT): Promise<number> {
  const total = await db.cached_isolates.count();
  if (total <= limit) return 0;

  const oldest = await db.cached_isolates
    .orderBy("seen_at")
    .limit(total - limit)
    .toArray();
  await db.cached_isolates.bulkDelete(oldest.map((row) => row.id));
  return oldest.length;
}

export async function cachedIsolates(limit = CACHED_ISOLATE_LIMIT): Promise<Isolate[]> {
  const rows = await db.cached_isolates.orderBy("seen_at").reverse().limit(limit).toArray();
  return rows.map((row) => row.isolate);
}

// ---------------------------------------------------------------------------
// session and reference data
// ---------------------------------------------------------------------------
export async function saveSession(
  accessToken: string | null,
  user: User | null,
): Promise<void> {
  await db.session.put({
    key: "current",
    access_token: accessToken,
    user,
    saved_at: Date.now(),
  });
}

export async function loadSession(): Promise<SessionRow | undefined> {
  return db.session.get("current");
}

export async function clearSession(): Promise<void> {
  await db.session.delete("current");
}

export async function saveReference(bundle: ReferenceBundle): Promise<void> {
  await db.reference_data.put({ key: "current", bundle, fetched_at: Date.now() });
}

export async function loadReference(): Promise<ReferenceBundle | null> {
  const row = await db.reference_data.get("current");
  return row?.bundle ?? null;
}

/**
 * Wipe everything local. Used on sign-out, so that signing out on a shared
 * device does not leave another volunteer's records readable.
 *
 * It refuses while work is still queued: that data exists nowhere else.
 */
export async function clearLocalData(options: { force?: boolean } = {}): Promise<boolean> {
  if (!options.force) {
    const unsynced = await db.isolates_local
      .where("state")
      .anyOf("pending", "syncing", "error", "conflict")
      .count();
    if (unsynced > 0) return false;
  }

  await Promise.all([
    db.isolates_local.clear(),
    db.cached_isolates.clear(),
    db.session.clear(),
    db.audit_queue.clear(),
  ]);
  // Reference data is not patient data and is expensive to refetch on a slow
  // connection, so it survives sign-out.
  return true;
}

// ---------------------------------------------------------------------------
// storage pressure (docs/09 §9.9)
// ---------------------------------------------------------------------------
export interface StorageStatus {
  usage: number;
  quota: number;
  ratio: number;
  /** Warn the volunteer. */
  nearFull: boolean;
  /** Refuse to save anything new until the queue drains. */
  full: boolean;
}

export async function storageStatus(): Promise<StorageStatus | null> {
  if (typeof navigator === "undefined" || !navigator.storage?.estimate) {
    return null;
  }
  const { usage = 0, quota = 0 } = await navigator.storage.estimate();
  const ratio = quota > 0 ? usage / quota : 0;
  return {
    usage,
    quota,
    ratio,
    nearFull: ratio >= 0.8,
    full: ratio >= 0.95,
  };
}

/**
 * Ask the browser to keep our data rather than evicting it under pressure.
 * Called once after the volunteer signs in; failure is not an error.
 */
export async function requestPersistence(): Promise<boolean> {
  if (typeof navigator === "undefined" || !navigator.storage?.persist) return false;
  try {
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
}
